import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useParams, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/projects/${id}`, { credentials: 'include' });
      const data = await response.json();
      setProject(data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-white">Loading...</div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout>
        <div className="p-8 text-white">Project not found</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <Link to="/admin/projects" className="text-[#C4A962] hover:underline text-sm mb-4 inline-block">
            ← Back to Projects
          </Link>
          <h1 className="text-3xl font-serif text-white mb-2">{project.reference}</h1>
          <p className="text-gray-400">{project.client_name} • {project.destination}</p>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
            <h2 className="text-xl font-serif text-white mb-4">Project Details</h2>
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-400">Client</div>
                <div className="text-white">{project.client_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Contact Person</div>
                <div className="text-white">{project.contact_person || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Destination</div>
                <div className="text-white">{project.destination}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400">Travel Start</div>
                  <div className="text-white">{project.travel_start_date}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">Travel End</div>
                  <div className="text-white">{project.travel_end_date}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400">Pax</div>
                  <div className="text-white">{project.pax}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">Rooms</div>
                  <div className="text-white">{project.rooms}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Status</div>
                <div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    project.status === 'Confirmed' ? 'bg-green-500/10 text-green-400' :
                    project.status === 'Quotation' ? 'bg-blue-500/10 text-blue-400' :
                    'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
            <h2 className="text-xl font-serif text-white mb-4">Financial Summary</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400 mb-1">Estimated Internal Cost</div>
                <div className="text-2xl font-bold text-white">
                  IDR {project.estimated_idr_cost?.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Pricing Method</div>
                <div className="text-white">
                  {project.pricing_method} {project.pricing_percentage}%
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Estimated IDR Selling Price</div>
                <div className="text-2xl font-bold text-[#C4A962]">
                  IDR {project.estimated_idr_selling_price?.toLocaleString()}
                </div>
              </div>
              <div className="pt-4 border-t border-[#1E293B]">
                <div className="text-sm text-gray-400 mb-1">Client Quotation</div>
                <div className="text-2xl font-bold text-white">
                  {project.quotation_currency} {project.quotation_amount?.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Exchange Rate: 1 {project.quotation_currency} = IDR {project.quotation_exchange_rate?.toLocaleString()}
                </div>
              </div>
              <div className="pt-4 border-t border-[#1E293B]">
                <div className="text-sm text-gray-400 mb-1">Expected Operating Profit</div>
                <div className="text-xl font-bold text-green-400">
                  IDR {(project.estimated_idr_selling_price - project.estimated_idr_cost)?.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Original Costing Reference */}
        <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
          <h2 className="text-xl font-serif text-white mb-4">Related</h2>
          <div className="flex items-center gap-4">
            <div className="text-gray-400">Original Costing:</div>
            <Link
              to={`/admin/costing/${project.costing_id}`}
              className="text-[#C4A962] hover:underline"
            >
              View Costing →
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
